import React, { useEffect, useState } from 'react';
import './AiAnalysis.scss';
import { useAxios } from 'hooks/useAxios';
import { getDataFromSingleEndpoint } from 'services/Torogoz/TogorozApi';
import { useStore as useGlobalStore } from 'store/useGlobalStore';

interface AiAnalysisProps {
  sessionId: string;
  isExternal: boolean;
}

const AiAnalysis: React.FC<AiAnalysisProps> = ({ sessionId, isExternal }) => {
  const { endpoints, dateFilter } = useGlobalStore((state) => ({
    endpoints: state.endpoints,
    dateFilter: state.dateFilter,
  }));

  const [analysisData, setAnalysisData] = useState<any>(null);
  const { response: analysis, axiosFetch, loading } = useAxios();

  useEffect(() => {
    if (!sessionId) return;

    const getAiAnalysisData = async () => {
      await getDataFromSingleEndpoint(
        endpoints.getWidgetData,
        {
          filters: {
            session_id: sessionId,
            date_interval: dateFilter,
          },
          source: 'chat',
          actionName: 'session-gpt-analysis',
          ...(isExternal
            ? {
                external: true,
              }
            : {
                external: false,
                mockEndpoint: 'data-session-gpt-analysis',
              }),
        },
        axiosFetch
      );
    };

    getAiAnalysisData();
  }, [sessionId, isExternal]);

  useEffect(() => {
    if (!analysis) return;
    setAnalysisData(analysis.data);
  }, [analysis]);

  const formatValue = (value: any) => {
    if (value === null || value === undefined) return 'N/A';
    if (value === '<<not sent to gpt>>') return 'Not analyzed';
    return value;
  };

  if (loading) {
    return <div className="ai-analysis-loading">Loading AI Analysis...</div>;
  }

  if (!analysisData) {
    return <div className="ai-analysis-error">No AI Analysis available</div>;
  }

  return (
    <div className="ai-analysis">
      <div className="ai-analysis_section">
        <h4 className="ai-analysis_label">Summary</h4>
        <p className="ai-analysis_content">{formatValue(analysisData.gpt_summary)}</p>
      </div>

      <div className="ai-analysis_section">
        <h4 className="ai-analysis_label">Contact Reason</h4>
        <p className="ai-analysis_content">{formatValue(analysisData.gpt_contact_reason)}</p>
      </div>

      <div className="ai-analysis_section">
        <h4 className="ai-analysis_label">Resolved</h4>
        <p className="ai-analysis_content">{analysisData.gpt_resolved ? 'True' : 'False'}</p>
      </div>

      <div className="ai-analysis_section">
        <h4 className="ai-analysis_label">Resolution</h4>
        <p className="ai-analysis_content">{formatValue(analysisData.gpt_resolution)}</p>
      </div>

      <div className="ai-analysis_section">
        <h4 className="ai-analysis_label">Sentiment</h4>
        <p className="ai-analysis_content">{formatValue(analysisData.gpt_sentiment_overall)}</p>
      </div>

      <div className="ai-analysis_section">
        <h4 className="ai-analysis_label">Disposition</h4>
        <p className="ai-analysis_content">{formatValue(analysisData.gpt_disposition)}</p>
      </div>

      <div className="ai-analysis_section">
        <h4 className="ai-analysis_label">Disposition Delta</h4>
        <p className="ai-analysis_content">{formatValue(analysisData.gpt_disposition_delta)}</p>
      </div>

      <div className="ai-analysis_section">
        <h4 className="ai-analysis_label">Disposition Delta Reason</h4>
        <p className="ai-analysis_content">{formatValue(analysisData.gpt_disposition_reason)}</p>
      </div>

      <div className="ai-analysis_section">
        <h4 className="ai-analysis_label">Tags</h4>
        <div className="ai-analysis_tags">
          {analysisData.gpt_tags && analysisData.gpt_tags.length > 0 ? (
            analysisData.gpt_tags.map((tag: string, index: number) => (
              <span key={index} className="ai-analysis_tag">
                {tag.replace(/_/g, ' ')}
              </span>
            ))
          ) : (
            <span className="ai-analysis_content">No tags</span>
          )}
        </div>
      </div>

      <div className="ai-analysis_section">
        <h4 className="ai-analysis_label">Transfers Bot to Agent</h4>
        <p className="ai-analysis_content">{analysisData.gpt_transfers_bot_agent || 0}</p>
      </div>

      <div className="ai-analysis_section">
        <h4 className="ai-analysis_label">Transfers Agent to Agent</h4>
        <p className="ai-analysis_content">{analysisData.gpt_transfers_agent_agent || 0}</p>
      </div>
    </div>
  );
};

export default AiAnalysis;
